import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, DatePicker, Dropdown, Select, Table, Tooltip, message } from 'antd';
import {
  DownOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import DataTable from '@/components/data-table';
import { API } from '@/services/api.service';
import CardHead from './CardHead';
import UnbookedInvoices from './UnbookedInvoices';
import { date, errorText, exportLabels, money, moneyOrBlank, saveBlob, str } from './format';

// The account a supplier card opens on: suppliers in Serbia.
const DEFAULT_SUPPLIER_ACCOUNT = '435000';
// The table writes the page into the address itself.
const noop = () => {};

/**
 * One analytic card from the books, laid out as the books print it, with the
 * invoices in progress under it.
 *
 * - `view="supplier"` (the supplier profile): the partner is fixed — the
 *   partner codes the books hold for the supplier's PIB — and only the
 *   account and period are chosen.
 * - `view="project"` (the "By project" tab): a project (org unit), optionally
 *   one account and one supplier.
 *
 * Filters and page live in the address (`from`, `to`, `account`, `partner`,
 * `orgUnit`, `offset`, `limit`; `uOffset`/`uLimit` for the invoices in
 * progress), so a link or a reload opens the same card.
 *
 * @param {Object} props
 * @param {string} props.company
 * @param {'supplier'|'project'} props.view
 * @param {Array<{code, name}>} [props.partners] - supplier view: the supplier's partner codes
 * @param {Object} props.queryParams
 * @param {Function} props.setQueryParams
 */
export default function LedgerCard({ company, view, partners: fixedPartners = [], queryParams, setQueryParams }) {
  const { t } = useTranslation(['ledgerCards', 'menus']);
  const [exporting, setExporting] = useState(null);
  const setFilters = (changes) => setQueryParams({ ...changes, offset: 1, uOffset: 1 });

  const { data: options, error: optionsError } = useQuery({
    queryKey: ['ledger-cards', company, 'options'],
    queryFn: () => API.ledgerCards.getOptions(company),
    enabled: Boolean(company),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const hasLedger = Boolean(options?.years?.length);

  // The period opens on the first day of the latest year held, up to the last posting.
  const lastYear = options?.years?.[options.years.length - 1] || dayjs().year();
  const from = str(queryParams.from) || `${lastYear}-01-01`;
  const to = str(queryParams.to) || options?.lastDate || dayjs().format('YYYY-MM-DD');

  const accounts = useMemo(() => options?.accounts || [], [options]);
  const partnerAccounts = useMemo(() => accounts.filter((a) => a.withPartners), [accounts]);
  const defaultAccount =
    (partnerAccounts.find((a) => a.account === DEFAULT_SUPPLIER_ACCOUNT) ||
      partnerAccounts.find((a) => a.account.startsWith('43')) ||
      partnerAccounts[0])?.account || '';
  const account = view === 'supplier' ? str(queryParams.account) || defaultAccount : str(queryParams.account);
  const orgUnit = view === 'project' ? str(queryParams.orgUnit) : '';
  const partner =
    view === 'supplier'
      ? (fixedPartners.find((p) => p.code === str(queryParams.partner)) || fixedPartners[0])?.code || ''
      : str(queryParams.partner);

  // Supplier accounts first (class 43), then every other account that carries partners.
  const accountOptions = useMemo(() => {
    const list = view === 'supplier' ? partnerAccounts : accounts;
    const label = (a) => [a.account, a.name].filter(Boolean).join(' ');
    const suppliers = list.filter((a) => a.account.startsWith('43'));
    const others = list.filter((a) => !a.account.startsWith('43'));
    return [
      suppliers.length && { label: t('views.supplier'), options: suppliers.map((a) => ({ value: a.account, label: label(a) })) },
      others.length && { label: t('filters.account'), options: others.map((a) => ({ value: a.account, label: label(a) })) },
    ].filter(Boolean);
  }, [accounts, partnerAccounts, view, t]);

  const projectOptions = (options?.projects || []).map((p) => ({
    value: p.code,
    label: [p.code, p.name].filter(Boolean).join(' '),
  }));

  const partnerScope = { orgUnit, account: account || undefined };
  const { data: projectPartners = [], isFetching: partnersLoading } = useQuery({
    queryKey: ['ledger-cards', company, 'partners', partnerScope],
    queryFn: () => API.ledgerCards.getPartners(company, partnerScope),
    enabled: Boolean(view === 'project' && company && hasLedger && orgUnit),
    staleTime: 5 * 60 * 1000,
  });
  const partnerOptions = (view === 'supplier' ? fixedPartners : projectPartners).map((p) => ({
    value: p.code,
    label: p.name ? `${p.code} · ${p.name}` : p.code,
  }));

  const ready = hasLedger && (view === 'supplier' ? Boolean(account && partner) : Boolean(orgUnit));
  const filters = { from, to, account: account || undefined, partner: partner || undefined, orgUnit: orgUnit || undefined };
  const page = { offset: str(queryParams.offset) || '1', limit: str(queryParams.limit) || '50' };

  const {
    data: card,
    isLoading: cardLoading,
    error: cardError,
  } = useQuery({
    queryKey: ['ledger-cards', company, 'card', filters, page],
    queryFn: () => API.ledgerCards.getCard(company, { ...filters, ...page }),
    enabled: Boolean(company && ready),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const download = async (format) => {
    setExporting(format);
    try {
      const { blob, fileName } = await API.ledgerCards.exportCard(company, filters, format, exportLabels(t));
      saveBlob(blob, fileName);
    } catch {
      message.error(t('export.failed'));
    } finally {
      setExporting(null);
    }
  };

  const fixedAccount = Boolean(card?.header?.account);
  const fixedPartner = Boolean(card?.header?.partner);
  const columns = [
    { title: t('columns.orderDate'), dataIndex: 'orderDate', key: 'orderDate', width: 100, render: date },
    { title: t('columns.orderNo'), dataIndex: 'orderNo', key: 'orderNo', width: 110, ellipsis: true },
    { title: t('columns.orgUnit'), dataIndex: 'orgUnit', key: 'orgUnit', width: 64 },
    !fixedAccount && { title: t('columns.account'), dataIndex: 'account', key: 'account', width: 84 },
    !fixedPartner && {
      title: t('columns.partner'),
      dataIndex: 'partner',
      key: 'partner',
      width: 200,
      ellipsis: { showTitle: false },
      render: (code, row) => (
        <Tooltip title={row.partnerName} placement="topLeft">
          <span>{[code, row.partnerName].filter(Boolean).join(' · ')}</span>
        </Tooltip>
      ),
    },
    {
      title: t('columns.description'),
      dataIndex: 'description',
      key: 'description',
      width: 260,
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v} placement="topLeft">
          <span>{v}</span>
        </Tooltip>
      ),
    },
    { title: t('columns.invoiceNo'), dataIndex: 'invoiceNo', key: 'invoiceNo', width: 150, ellipsis: true },
    { title: t('columns.debit'), dataIndex: 'debit', key: 'debit', width: 130, align: 'right', render: moneyOrBlank },
    { title: t('columns.credit'), dataIndex: 'credit', key: 'credit', width: 130, align: 'right', render: moneyOrBlank },
    card?.hasFx && { title: t('columns.currency'), dataIndex: 'currency', key: 'currency', width: 80 },
    card?.hasFx && { title: t('columns.debitFx'), dataIndex: 'debitFx', key: 'debitFx', width: 120, align: 'right', render: moneyOrBlank },
    card?.hasFx && { title: t('columns.creditFx'), dataIndex: 'creditFx', key: 'creditFx', width: 120, align: 'right', render: moneyOrBlank },
    { title: t('columns.contract'), dataIndex: 'contract', key: 'contract', width: 90, ellipsis: true },
    { title: t('columns.entryDate'), dataIndex: 'entryDate', key: 'entryDate', width: 100, render: date },
    { title: t('columns.documentDate'), dataIndex: 'documentDate', key: 'documentDate', width: 100, render: date },
    { title: t('columns.dueDate'), dataIndex: 'dueDate', key: 'dueDate', width: 100, render: date },
    { title: t('columns.vatChange'), dataIndex: 'vatChange', key: 'vatChange', width: 90, ellipsis: true },
    { title: t('columns.operator'), dataIndex: 'operator', key: 'operator', width: 110, ellipsis: true },
  ].filter(Boolean);
  const tableWidth = columns.reduce((s, c) => s + (c.width || 0), 0);

  if (optionsError) return <Alert type="error" showIcon message={errorText(t, optionsError)} />;
  if (options && !hasLedger) return <Alert type="warning" showIcon message={t('noLedger')} />;
  if (view === 'supplier' && options && !fixedPartners.length) {
    return <Alert type="info" showIcon message={t('noPartnerInBooks')} />;
  }

  return (
    <div className="grid gap-4">
      {options?.lastDate && <p className="m-0 text-sm opacity-70">{t('dataAsOf', { date: date(options.lastDate) })}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {view === 'project' && (
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 260 }}
            placeholder={t('filters.project')}
            value={orgUnit || undefined}
            onChange={(v) => setFilters({ orgUnit: v, partner: undefined })}
            options={projectOptions}
          />
        )}
        <Select
          showSearch
          allowClear={view === 'project'}
          optionFilterProp="label"
          style={{ width: 320 }}
          popupMatchSelectWidth={false}
          placeholder={view === 'project' ? t('filters.anyAccount') : t('filters.account')}
          value={account || undefined}
          onChange={(v) => setFilters({ account: v || undefined, ...(view === 'project' ? { partner: undefined } : {}) })}
          options={accountOptions}
        />
        {(view === 'project' || fixedPartners.length > 1) && (
          <Select
            showSearch
            allowClear={view === 'project'}
            optionFilterProp="label"
            style={{ width: 360 }}
            popupMatchSelectWidth={false}
            loading={partnersLoading}
            disabled={view === 'project' && !orgUnit}
            placeholder={view === 'project' ? t('filters.anyPartner') : t('filters.partner')}
            value={partner || undefined}
            onChange={(v) => setFilters({ partner: v || undefined })}
            options={partnerOptions}
          />
        )}
        <DatePicker.RangePicker
          format="DD.MM.YYYY"
          allowClear={false}
          placeholder={[t('filters.from'), t('filters.to')]}
          value={[dayjs(from), dayjs(to)]}
          onChange={(range) => range && setFilters({ from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') })}
        />
      </div>

      {!ready && options && <Alert type="info" showIcon message={t(view === 'supplier' ? 'pickSupplier' : 'pickProject')} />}
      {ready && cardError && <Alert type="error" showIcon message={errorText(t, cardError)} />}
      {ready && card?.beforeCoverage && <Alert type="warning" showIcon message={t('coverage', { date: date(card.coverageFrom) })} />}
      {ready && card && <CardHead card={card} />}

      {ready && (
        <DataTable
          title={`${t('lines')}${card ? ` · ${card.count}` : ''}`}
          data={card?.items || []}
          total={card?.count || 0}
          status={cardLoading ? 'pending' : cardError ? 'error' : 'success'}
          columns={columns}
          offset={page.offset}
          limit={page.limit}
          onChangePagination={noop}
          tableLayout="fixed"
          scroll={{ x: tableWidth }}
          size="small"
          summary={() => {
            if (!card) return null;
            const debitAt = columns.findIndex((c) => c.key === 'debit');
            return (
              <Table.Summary.Row className="font-semibold">
                <Table.Summary.Cell index={0} colSpan={debitAt}>
                  {t('blocks.period')}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={debitAt} align="right">
                  {money(card.period.debit)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={debitAt + 1} align="right">
                  {money(card.period.credit)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={debitAt + 2} colSpan={columns.length - debitAt - 2} />
              </Table.Summary.Row>
            );
          }}
          headerChildren={
            // One button for every file, as the invoice books have it.
            <Dropdown
              trigger={['click']}
              disabled={!card || Boolean(exporting)}
              menu={{
                items: [
                  { key: 'xlsx', icon: <FileExcelOutlined />, label: t('export.excel'), onClick: () => download('xlsx') },
                  { key: 'pdf', icon: <FilePdfOutlined />, label: t('export.pdf'), onClick: () => download('pdf') },
                ],
              }}
            >
              <Button
                type="default"
                shape="round"
                className="btn-default h-8 shrink-0 border-white text-black hover:border-white"
                icon={exporting ? <LoadingOutlined className="text-zinc-400" spin /> : <DownloadOutlined className="text-zinc-400" />}
              >
                <span>{t('export', { ns: 'menus' })}</span>
                <DownOutlined className="text-[10px] text-zinc-400" />
              </Button>
            </Dropdown>
          }
        />
      )}

      {ready && card && (
        <UnbookedInvoices company={company} card={card} filters={filters} queryParams={queryParams} setQueryParams={setQueryParams} />
      )}
    </div>
  );
}
